import { Link } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import type { PropsWithChildren } from "react"
import { useEffect, useRef } from "react"

import { Button } from "@/components/ui/button"
import { rootPath } from "@/routes"

interface AuthLayoutProps {
  name?: string
  title?: string
  description?: string
}

export default function AuthSimpleLayout({
  children,
  title,
  description,
}: PropsWithChildren<AuthLayoutProps>) {
  const containerRef = useRef<HTMLDivElement>(null)

  // Lock body scroll on mobile (prevents iOS from scrolling body behind layout)
  useEffect(() => {
    if (window.innerWidth >= 768) return
    const orig = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = orig
    }
  }, [])

  // Keyboard: Capacitor native events (resize: 'none' means visualViewport doesn't work)
  useEffect(() => {
    if (window.innerWidth >= 768) return

    let showListener: { remove: () => Promise<void> } | undefined
    let hideListener: { remove: () => Promise<void> } | undefined
    let cleanupVisualViewport: (() => void) | undefined

    const setup = async () => {
      try {
        const { Keyboard } = await import("@capacitor/keyboard")

        showListener = await Keyboard.addListener(
          "keyboardWillShow",
          (info) => {
            const el = containerRef.current
            if (!el) return
            el.style.height = `${window.innerHeight - info.keyboardHeight}px`
          },
        )

        hideListener = await Keyboard.addListener("keyboardWillHide", () => {
          const el = containerRef.current
          if (!el) return
          el.style.removeProperty("height")
        })
      } catch {
        // Not in Capacitor — use visualViewport fallback (works in Safari browser)
        const vv = window.visualViewport
        if (!vv) return

        const update = () => {
          const el = containerRef.current
          if (!el) return
          const keyboardOpen = vv.height < window.innerHeight - 100
          if (keyboardOpen) {
            el.style.height = `${vv.height}px`
            el.style.top = `${vv.offsetTop}px`
          } else {
            el.style.removeProperty("height")
            el.style.removeProperty("top")
          }
        }

        vv.addEventListener("resize", update)
        vv.addEventListener("scroll", update)
        cleanupVisualViewport = () => {
          vv.removeEventListener("resize", update)
          vv.removeEventListener("scroll", update)
        }
      }
    }

    void setup()

    return () => {
      void showListener?.remove()
      void hideListener?.remove()
      cleanupVisualViewport?.()
    }
  }, [])

  return (
    <div
      ref={containerRef}
      className="bg-background pt-safe pb-safe fixed inset-0 flex flex-col overflow-hidden md:relative md:inset-auto md:min-h-svh md:overflow-visible md:pt-0"
    >
      <div className="pb-safe min-h-0 flex-1 overflow-y-auto overscroll-contain md:flex md:items-center md:justify-center md:overflow-visible md:pb-0">
        <div className="mx-auto flex w-full max-w-md flex-col gap-8 px-6 py-8 md:px-0">
          {/* Back button (mobile) */}
          <div className="flex md:hidden">
            <Link href={rootPath()} className="inline-flex" prefetch>
              <Button variant="outline" size="sm">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Volver 🏠
              </Button>
            </Link>
          </div>

          {/* CurrAR Branding */}
          <div className="flex flex-col items-center gap-6">
            <Link
              href={rootPath()}
              className="flex flex-col items-center gap-3 font-medium"
            >
              <img
                src="/icon_transparent.png"
                alt="CurrAR"
                className="h-16 w-16"
              />

              <div className="flex flex-col items-center">
                <span className="text-foreground text-2xl leading-tight font-bold tracking-tight">
                  CurrAR
                </span>
                <span className="text-muted-foreground text-sm font-medium">
                  Comunidad en Madrid
                </span>
              </div>
            </Link>

            {/* Social Proof for Sign-up */}
            {title?.includes("Crear") && (
              <div className="bg-muted/30 border-border w-full rounded-xl border p-4 text-center">
                <p className="text-foreground mb-3 text-sm font-semibold">
                  🇦🇷 La comunidad argentina más grande de Madrid
                </p>
                <div className="text-muted-foreground grid grid-cols-2 gap-3 text-xs">
                  <div>👥 2.5K+ Argentinos</div>
                  <div>🎉 150+ Eventos/mes</div>
                  <div>💼 50+ Empresas</div>
                  <div>🏘️ 28 Barrios</div>
                </div>
              </div>
            )}

            <div className="space-y-2 text-center">
              <h1 className="text-xl font-semibold">{title}</h1>
              <p className="text-muted-foreground text-center text-sm">
                {description}
              </p>
            </div>
          </div>
          {children}

          {/* Trust indicators */}
          <div className="text-muted-foreground text-center text-xs">
            Gratis para siempre • Verificado •{" "}
            {title?.includes("Crear") ? "2,500+" : "Seguro"} miembros
          </div>
        </div>
      </div>
    </div>
  )
}
